import { Vector2 } from '../../../types/game.types';
import { Enemy, EnemyType } from '../entities/Enemy';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class EnemyFormation {
  private enemies: Enemy[] = [];
  private formationPosition: Vector2;
  private direction: number = 1; // 1 for right, -1 for left
  private speed: number = 30; // pixels per second horizontal movement
  private dropDistance: number = 20; // pixels to drop when hitting edge
  private lastMoveTime: number = 0;
  private moveInterval: number = 0.5; // seconds between moves
  private baseMoveInterval: number = 0.5; // base interval for the current level
  private gameWidth: number;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private dropsCompleted: number = 0; // Track how many times formation has dropped
  
  // Formation layout constants
  private readonly ENEMY_SPACING_X = 32;
  private readonly ENEMY_SPACING_Y = 24;
  private readonly FORMATION_MARGIN = 40;

  constructor(renderManager: RenderManager, audioManager: AudioManager, gameWidth: number) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    this.formationPosition = { x: this.FORMATION_MARGIN, y: 60 };
  }

  public createFormation(level: number = 1): void {
    this.enemies = [];
    
    // Calculate starting position based on authentic Space Invaders rules
    // Screen 1: start at top (y=60)
    // Screens 2-9: progressively start lower (each screen starts 1 drop lower)
    // Screen 10+: cycle back to screen 1 position
    const screenNumber = ((level - 1) % 9) + 1; // Screens 1-9, then cycle
    const baseY = 60;
    const progressiveOffset = (screenNumber - 1) * this.dropDistance; // Each screen starts 1 drop lower
    
    const startX = this.formationPosition.x;
    const startY = baseY + progressiveOffset;
    
    // Determine formation size based on level - much larger formations
    const cols = Math.min(13, 9 + Math.floor(level / 2)); // Start with 9, add columns every 2 levels
    const rows = Math.min(7, 5 + Math.floor(level / 3)); // Start with 5 rows, add rows every 3 levels
    
    // Determine enemy type distribution based on level - more aggressive scaling
    const advancedRows = Math.min(rows, 1 + Math.floor(level / 3)); // More advanced enemies sooner
    const mediumRows = Math.min(rows - advancedRows, Math.max(1, 3 - Math.floor(level / 4))); // Fewer basic enemies
    const basicRows = rows - advancedRows - mediumRows;
    
    let currentRow = 0;
    
    // Create advanced enemies (red, top rows) - 30 points
    for (let row = 0; row < advancedRows; row++) {
      for (let col = 0; col < cols; col++) {
        const enemy = new Enemy(
          `enemy_advanced_${row}_${col}`,
          startX + col * this.ENEMY_SPACING_X,
          startY + currentRow * this.ENEMY_SPACING_Y,
          EnemyType.ADVANCED,
          this.renderManager,
          this.audioManager
        );
        // Higher level enemies might be tougher (future enhancement: add health)
        this.enemies.push(enemy);
      }
      currentRow++;
    }
    
    // Create medium enemies (yellow) - 20 points
    for (let row = 0; row < mediumRows; row++) {
      for (let col = 0; col < cols; col++) {
        const enemy = new Enemy(
          `enemy_medium_${row}_${col}`,
          startX + col * this.ENEMY_SPACING_X,
          startY + currentRow * this.ENEMY_SPACING_Y,
          EnemyType.MEDIUM,
          this.renderManager,
          this.audioManager
        );
        this.enemies.push(enemy);
      }
      currentRow++;
    }
    
    // Create basic enemies (white) - 10 points
    for (let row = 0; row < basicRows; row++) {
      for (let col = 0; col < cols; col++) {
        const enemy = new Enemy(
          `enemy_basic_${row}_${col}`,
          startX + col * this.ENEMY_SPACING_X,
          startY + currentRow * this.ENEMY_SPACING_Y,
          EnemyType.BASIC,
          this.renderManager,
          this.audioManager
        );
        this.enemies.push(enemy);
      }
      currentRow++;
    }
    
    console.log(`Level ${level} formation: ${cols}x${rows} grid, ${this.enemies.length} enemies (${advancedRows} advanced, ${mediumRows} medium, ${basicRows} basic)`);
    
    // Adjust speed based on level
    this.adjustSpeedForLevel(level);
  }

  public update(deltaTime: number): void {
    this.lastMoveTime += deltaTime;
    
    if (this.lastMoveTime >= this.moveInterval) {
      this.moveFormation();
      this.lastMoveTime = 0;
      
      // Speed up as fewer enemies remain
      this.adjustMoveInterval();
    }
    
    // Update all enemies
    this.enemies.forEach(enemy => {
      if (enemy.active) {
        enemy.update(deltaTime);
      }
    });
  }

  private moveFormation(): void {
    const activeEnemies = this.getActiveEnemies();
    if (activeEnemies.length === 0) return;
    
    // Check if we need to change direction and drop down
    const shouldDrop = this.shouldDropDown();
    
    if (shouldDrop) {
      this.dropDown();
      this.direction *= -1; // Change direction
    } else {
      this.moveHorizontally();
    }
  }

  private shouldDropDown(): boolean {
    const activeEnemies = this.getActiveEnemies();
    if (activeEnemies.length === 0) return false;
    
    // Find the leftmost and rightmost enemies
    let leftmost = activeEnemies[0];
    let rightmost = activeEnemies[0];
    
    activeEnemies.forEach(enemy => {
      if (enemy.position.x < leftmost.position.x) leftmost = enemy;
      if (enemy.position.x > rightmost.position.x) rightmost = enemy;
    });
    
    // Check if we're hitting the edges
    if (this.direction > 0) {
      return rightmost.position.x + rightmost.size.x >= this.gameWidth - this.FORMATION_MARGIN;
    } else {
      return leftmost.position.x <= this.FORMATION_MARGIN;
    }
  }

  private dropDown(): void {
    this.enemies.forEach(enemy => {
      if (enemy.active) {
        enemy.position.y += this.dropDistance;
      }
    });
    
    // Track drops for authentic shooting behavior
    this.dropsCompleted++;
    console.log(`Formation dropped! Drops completed: ${this.dropsCompleted}, Can shoot missiles: ${this.dropsCompleted >= 2}`);
  }

  private moveHorizontally(): void {
    // Authentic Space Invaders: asymmetric speed - faster left-to-right than right-to-left
    const baseDistance = this.speed * this.moveInterval;
    let moveDistance: number;
    
    if (this.direction > 0) {
      // Moving right: normal speed
      moveDistance = baseDistance;
    } else {
      // Moving left: slightly slower (multiply by 0.9)
      moveDistance = baseDistance * 0.9;
    }
    
    this.enemies.forEach(enemy => {
      if (enemy.active) {
        enemy.position.x += moveDistance * this.direction;
      }
    });
  }

  private adjustMoveInterval(): void {
    const activeCount = this.getActiveEnemies().length;
    const totalEnemies = this.enemies.length; // Use actual enemy count
    
    // MUCH more aggressive speed increase as enemies are destroyed
    // When all enemies alive: multiplier = 1.0
    // When half destroyed: multiplier = 0.3 (3.3x faster)
    // When only 1 enemy left: multiplier = 0.08 (12.5x faster!)
    const speedMultiplier = Math.max(0.08, Math.pow(activeCount / totalEnemies, 2.0)); // More aggressive curve
    
    // Apply to BASE interval for this level (not the current modified interval)
    this.moveInterval = this.baseMoveInterval * speedMultiplier;
    
    // Ensure minimum speed cap - made even more aggressive
    this.moveInterval = Math.max(0.03, this.moveInterval);
  }

  private adjustSpeedForLevel(level: number): void {
    // Aggressive difficulty scaling
    // Base speed increases significantly per level
    this.speed = 30 + (level - 1) * 15; // Increased from 8 to 15
    
    // Move interval decreases much more aggressively (enemies move MUCH more frequently)
    // Starts at 0.4s, decreases by 0.06s per level, minimum 0.05s
    this.baseMoveInterval = Math.max(0.05, 0.4 - (level - 1) * 0.06); // More aggressive
    this.moveInterval = this.baseMoveInterval; // Initialize current interval
    
    // Drop distance increases more aggressively (more threatening descent)
    this.dropDistance = 25 + Math.floor(level / 2) * 8; // Increased base and scaling
    
    console.log(`Level ${level} formation: speed=${this.speed}px/s, baseInterval=${this.baseMoveInterval.toFixed(2)}s, drop=${this.dropDistance}px`);
  }

  public getActiveEnemies(): Enemy[] {
    return this.enemies.filter(enemy => enemy.active);
  }

  public getAllEnemies(): Enemy[] {
    return this.enemies;
  }

  public getRandomActiveEnemy(): Enemy | null {
    const activeEnemies = this.getActiveEnemies();
    if (activeEnemies.length === 0) return null;
    
    return activeEnemies[Math.floor(Math.random() * activeEnemies.length)];
  }

  public getBottomRowEnemies(): Enemy[] {
    const activeEnemies = this.getActiveEnemies();
    if (activeEnemies.length === 0) return [];
    
    // Group enemies by column
    const columnGroups: { [key: number]: Enemy[] } = {};
    
    activeEnemies.forEach(enemy => {
      const col = Math.round(enemy.position.x / this.ENEMY_SPACING_X);
      if (!columnGroups[col]) columnGroups[col] = [];
      columnGroups[col].push(enemy);
    });
    
    // Get the bottom enemy from each column
    const bottomEnemies: Enemy[] = [];
    Object.values(columnGroups).forEach(columnEnemies => {
      let bottomEnemy = columnEnemies[0];
      columnEnemies.forEach(enemy => {
        if (enemy.position.y > bottomEnemy.position.y) {
          bottomEnemy = enemy;
        }
      });
      bottomEnemies.push(bottomEnemy);
    });
    
    return bottomEnemies;
  }

  public isEmpty(): boolean {
    return this.getActiveEnemies().length === 0;
  }

  public hasReachedBottom(bottomY: number): boolean {
    const activeEnemies = this.getActiveEnemies();
    return activeEnemies.some(enemy => 
      enemy.position.y + enemy.size.y >= bottomY
    );
  }

  public canShootMissiles(): boolean {
    // Authentic Space Invaders: enemies only start shooting after 2 drops (adjusted for faster gameplay)
    return this.dropsCompleted >= 2;
  }

  public reset(): void {
    this.enemies = [];
    this.direction = 1;
    this.formationPosition = { x: this.FORMATION_MARGIN, y: 60 };
    this.lastMoveTime = 0;
    this.moveInterval = 0.5;
    this.baseMoveInterval = 0.5;
    this.dropsCompleted = 0;
  }
}